import { Directive, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import {
  AddedDocToSignResponse,
  DictionaryField,
  DocKeyId,
  DocSign,
  DocSignService,
  DocSignStatus,
  FieldId,
  FormAlertService,
  MapAction,
  MapComponent,
  MapObjectApiType,
  MapObjectTableActionType,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  MapViewActionType,
  PetentData,
  Place,
  PortalId,
  PzService,
  RequestModeControlName,
  ShapeUtils,
  Street,
  StreetService,
  ToolType,
  WebPortal,
  Wkt,
  defaultMapGeometryStyles,
  initialToolsStateWithButtonText,
  validateFloatInput,
  validateIntegerInput,
} from '@gk/gk-modules';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { FileSaverService } from 'ngx-filesaver';
import { ToastrService } from 'ngx-toastr';
import { WKT } from 'ol/format';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeWhile,
} from 'rxjs';
import { NewRequestModel } from '../../modules/free-services-portal/services/new-request/new-request.model';
import { WebPortalResolver } from '../../resolvers/web-portal/web-portal.resolver';
import { NewRequestHelperService } from '../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../services/place/place.service';
import { BsMessageType } from '../../services/request-workspace-state/request-workspace-state.model';
import { DateHelperUtil } from '../../utils/date-helper/date-helper.util';

@Directive()
export abstract class BaseNewRequestComponent implements OnInit {
  readonly initialToolsState = _.cloneDeep(initialToolsStateWithButtonText);
  readonly defaultMapGeometryStyles = _.cloneDeep(defaultMapGeometryStyles);
  isAlive = true;
  petentData = {} as PetentData;
  model: NewRequestModel;
  docSignUrl: SafeUrl = '';
  docSignMsgType: BsMessageType;
  docSignMsgTxt = '';
  docSignTranslations: Record<string, string> = {};
  docSignPending = false;
  wrongFilesErrorText: string;
  mapState = MapState.getInitialStruct();
  formGroup: UntypedFormGroup;
  controlName: any;
  submitted: boolean;
  portalId: PortalId;
  errorSubmitMessage = '';
  currentDate = DateHelperUtil.getCurrentNgbDate();
  @ViewChild(MapComponent, { read: ElementRef }) mapComponent: ElementRef;

  constructor(
    protected pzService?: PzService,
    protected newRequestHelperService?: NewRequestHelperService,
    protected docSignService?: DocSignService,
    protected router?: Router,
    protected translateService?: TranslateService,
    protected mapSettingsService?: MapSettingsService,
    protected mapStateService?: MapStateService,
    protected formAlertService?: FormAlertService,
    protected placeService?: PlaceService,
    protected streetService?: StreetService,
    protected fileSaverService?: FileSaverService,
    protected toastrService?: ToastrService,
  ) {}

  ngOnInit(): void {
    this.subscribeToPetentData();
    this.subscribeToDocSignTranslations();
  }

  subscribeToPetentData(): void {
    this.pzService
      .getPetentData()
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((petentData) => (this.petentData = petentData));
  }

  subscribeToDocSignTranslations(): void {
    this.translateService
      .get('DOC_SIGN')
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((translations) => (this.docSignTranslations = translations));
  }

  fetchWrongFileText(): void {
    this.translateService
      .get('WRONG_FILES')
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((text) => (this.wrongFilesErrorText = text));
  }

  fetchApplicantMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.Applicant)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.setMapState(mapSettings);
      });
  }

  protected setMapState?(mapSettings: MapSettings): void;

  showOnMap({
    geom,
    scrollToMap = false,
    scrollCallerElementRef,
  }: {
    geom: Wkt;
    scrollToMap: boolean;
    scrollCallerElementRef?: ElementRef;
  }): void {
    const mapObject = ShapeUtils.getAnyGeometryMapObject(
      geom,
      MapObjectApiType.ExtentOrPolygon,
    );
    const extent = new WKT().readGeometry(mapObject.geom).getExtent();
    this.handleMapAction(
      new MapAction(MapViewActionType.ExtentToFitToChange, extent),
    );
    this.handleMapAction(
      new MapAction(MapObjectTableActionType.Select, mapObject),
    );
    if (scrollToMap) {
      this.scrollToMap(scrollCallerElementRef);
    }
  }

  scrollToMap(elementRef: ElementRef): void {
    this.mapComponent.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
    this.handleMapAction(
      new MapAction(MapViewActionType.BackScrollIntoViewRefChange, elementRef),
    );
  }

  handleMapAction(mapAction: MapAction): void {
    this.mapState = this.mapStateService.getUpdatedMapState(
      mapAction,
      this.mapState,
    );
  }

  handleSendAndValidateFailure(error?: Error): void {
    if (error) {
      console.error(error);
    }
    this.setDocSignMsg(BsMessageType.Danger, 'SENDING_FAILED');
    this.docSignPending = false;
  }

  getShippingFormGroup(): UntypedFormGroup {
    return this.formGroup.get(
      this.controlName.ShippingForm,
    ) as UntypedFormGroup;
  }

  handleSendAndValidateSuccess(
    addedDocToSignResponse: AddedDocToSignResponse,
  ): void {
    this.docSignPending = true;
    this.docSignUrl = this.newRequestHelperService.sanitizeDocSignUrl(
      addedDocToSignResponse.docSigningUrl,
    );
    this.openSigningWindow(addedDocToSignResponse.docSigningUrl);
    this.waitForDocSign(addedDocToSignResponse.docKeyId);
  }

  openSigningWindow(windowUrl: SafeUrl): void {
    this.newRequestHelperService.openWindowWithSafeUrl(windowUrl);
  }

  waitForDocSign(docKeyId: DocKeyId): void {
    this.setDocSignMsg(BsMessageType.Info, 'WAITING_FOR_SIGN');

    this.docSignService
      .setDocSignStatusCheckInterval(docKeyId)
      .pipe(
        takeWhile(() => this.isAlive),
        distinctUntilChanged(_.isEqual),
      )
      .subscribe((docSignOrError: DocSign | Error) =>
        this.handleDocSignChange(docSignOrError),
      );
  }

  handleDocSignChange(docSignOrError: DocSign | Error): void {
    switch (_.get(docSignOrError, 'status')) {
      case DocSignStatus.Success:
        this.handleDocSignSuccess();
        break;
      case DocSignStatus.Pending:
        this.setDocSignMsg(BsMessageType.Info, 'WAITING_FOR_SIGN');
        break;
      case DocSignStatus.Canceled:
        this.setDocSignMsg(BsMessageType.Danger, 'SIGN_CANCELED');
        this.docSignPending = false;
        this.docSignUrl = '';
        break;
      case DocSignStatus.Failure:
      default:
        this.handleDocSignFailure();
        break;
    }
  }

  handleDocSignSuccess(): void {
    this.docSignUrl = '';
    this.router.navigateByUrl(
      this.router.url.replace('new-request', 'new-request-summary'),
    );
  }

  handleDocSignFailure(): void {
    this.setDocSignMsg(BsMessageType.Danger, 'SIGN_FAILED');
    this.docSignPending = false;
    this.docSignUrl = '';
  }

  setDocSignMsg(type: BsMessageType, translationKey: string): void {
    this.docSignMsgType = type;
    this.docSignMsgTxt = this.docSignTranslations[translationKey];
  }

  shouldShowRequiredAlert(formControl: AbstractControl): boolean {
    return this.formAlertService.shouldShowErrorAlert(
      formControl,
      'required',
      this.submitted,
    );
  }

  shouldShowMinMaxAlert(formControl: AbstractControl): boolean {
    return (
      this.formAlertService.shouldShowErrorAlert(
        formControl,
        'min',
        this.submitted,
      ) ||
      this.formAlertService.shouldShowErrorAlert(
        formControl,
        'max',
        this.submitted,
      )
    );
  }

  searchPlace = ($term: Observable<string>): Observable<Place[]> =>
    $term.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => this.placeService.getLocalPlacesByTerm(term)),
    );

  formatter = (dictField: DictionaryField): string => dictField.name;

  searchStreet = ($term: Observable<string>): Observable<Street[]> =>
    $term.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) =>
        this.streetService.getStreets(term, this.getPlaceId()),
      ),
    );

  getPlaceId(): number | undefined {
    const place = this.getPlaceFormControl().value;

    return place && place.id;
  }

  setPlaceByName(): void {
    const formControl = this.getPlaceFormControl();
    if (!_.get(formControl.value, 'id') && _.isString(formControl.value)) {
      this.placeService
        .getLocalPlacesByTerm(formControl.value)
        .pipe(
          takeWhile(() => this.isAlive),
          takeWhile(
            () =>
              !_.get(formControl.value, 'id') && _.isString(formControl.value),
          ),
        )
        .subscribe((places) => {
          if (
            _.get(places, '[0].name', '').toUpperCase() ===
            formControl.value.toUpperCase()
          ) {
            formControl.setValue(places[0]);
          } else {
            formControl.setValue(
              new Place(undefined, formControl.value, undefined),
            );
          }
        });
    }
  }

  setStreetByName(): void {
    const formControl = this.getStreetFormControl();
    if (!_.get(formControl.value, 'id') && _.isString(formControl.value)) {
      this.streetService
        .getStreets(formControl.value, this.getPlaceId())
        .pipe(
          takeWhile(() => this.isAlive),
          takeWhile(
            () =>
              !_.get(formControl.value, 'id') && _.isString(formControl.value),
          ),
        )
        .subscribe((streets) => {
          if (
            _.get(streets, '[0].name', '').toUpperCase() ===
            formControl.value.toUpperCase()
          ) {
            formControl.setValue(streets[0]);
          } else {
            formControl.setValue(new Street(undefined, formControl.value));
          }
        });
    }
  }

  getStreetFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.Street);
  }

  getPlaceFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.Place);
  }

  isLawPersonFormGroupValid(): boolean {
    return this.getLawPersonFormGroup().valid;
  }

  getLawPersonFormGroup(): UntypedFormGroup {
    return this.formGroup.get(this.controlName.LawPerson) as UntypedFormGroup;
  }

  isInvestorDetailsFormGroupValid(): boolean {
    return this.getInvestorDetailsFormGroup().valid;
  }

  getInvestorDetailsFormGroup(): UntypedFormGroup {
    return this.formGroup.get(
      this.controlName.InvestorDetails,
    ) as UntypedFormGroup;
  }

  getContactDataFormGroup(): UntypedFormGroup {
    return this.formGroup.get(this.controlName.ContactData) as UntypedFormGroup;
  }

  setPortalIdFromWebPortalResolver(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
  ): void {
    const webPortal: WebPortal = _.get(
      activatedRouteSnapshot.data,
      WebPortalResolver.field,
    );
    this.portalId = webPortal.portalId;
  }

  validateIntegerInput(event: KeyboardEvent): void {
    if (!validateIntegerInput(event.key)) {
      event.preventDefault();
    }
  }

  validateFloatInput(event: KeyboardEvent): void {
    if (!validateFloatInput(event.key)) {
      event.preventDefault();
    }
  }

  getActiveToolType(mapState?: MapState): ToolType | null {
    const targetMapState = mapState || this.mapState;
    return (
      (Object.keys(targetMapState.toolsState).find(
        (object: unknown) =>
          targetMapState.toolsState[object as ToolType]?.isActive,
      ) as ToolType) || null
    );
  }

  validateIfRequestModeIsSelectedAndShowAlert = (): boolean => {
    const isSelected = this.getRequestModeValue();
    if (!isSelected) {
      this.translateService
        .get('REQUEST_MODE.REQUIRED')
        .pipe(takeWhile(() => this.isAlive))
        .subscribe((text) => {
          this.toastrService?.warning(text);
        });
    }

    return !!isSelected;
  };

  getRequestModeValue(): FieldId {
    return this.getRequestModeFormControl().value;
  }

  handleRequestModeChange(): void {
    const control = this.getRequestModeFormControl();
    control?.valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((requestMode: any) => {
        this.onRequestModeChange?.(requestMode);
      });
  }

  protected onRequestModeChange?(requestMode: any): void;

  getRequestModeFormControl(): AbstractControl {
    return this.formGroup.get(RequestModeControlName.RequestMode);
  }

  preventDateFromPast(dateFormControl: AbstractControl): void {
    dateFormControl.valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((date: NgbDate) => {
        if (
          date &&
          DateHelperUtil.compareTwoDates(date, this.currentDate) < 0
        ) {
          dateFormControl.setValue(this.currentDate, {
            emitEvent: false,
          });
        }
      });
  }

  onBankAccountInput(event: Event, control: AbstractControl): void {
    const input = event.target as HTMLInputElement;
    if (!control) return;

    const originalValue = input.value;
    const originalCursorPos = input.selectionStart ?? 0;

    const formatted = this.formatBankAccountNumber(originalValue);

    const lengthDiff = formatted.length - originalValue.length;

    control.setValue(formatted, {
      emitEvent: false,
    });

    setTimeout(() => {
      let newCursorPos = originalCursorPos + lengthDiff;

      if (newCursorPos < 0) newCursorPos = 0;
      if (newCursorPos > formatted.length) newCursorPos = formatted.length;

      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }

  formatBankAccountNumber(value: string): string {
    if (!value) return '';

    // Remove all non-letter/digit characters and make uppercase
    const clean = value.replace(/\W+/g, '').toUpperCase();

    // POLISH ACCOUNT: 26 digits, or 28 chars with 'PL'
    if (/^(\d{26}|PL\d{26})$/.test(clean)) {
      let digits = clean;
      if (digits.startsWith('PL')) digits = digits.slice(2);

      // 2+4+4+4+4+4+4 (NRB)
      return [
        digits.slice(0, 2),
        digits.slice(2, 6),
        digits.slice(6, 10),
        digits.slice(10, 14),
        digits.slice(14, 18),
        digits.slice(18, 22),
        digits.slice(22, 26),
      ]
        .filter(Boolean)
        .join(' ');
    }

    // FOR IBAN (other countries or unknown length): group into 4-char blocks
    return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
  }
}
